<template>
  <Listbox as="div" v-model="value" by="id" :disabled="fetching">
    <ListboxButton class="listbox-button" v-if="fetching">
      <span class="flex items-center text-gray-500">
          Lade Methoden...
      </span>
      <span class="pointer-events-none absolute inset-y-0 right-0 ml-3 flex items-center pr-2">
        <Spinner class="h-5 w-5 text-gray-400" aria-hidden="true" />
      </span>
    </ListboxButton>
    <ListboxButton class="listbox-button" v-else="fetching">
      <span v-if="value" class="flex items-center">
        <Twemoji :code="value.emoji" class="h-6 w-6 flex-shrink-0 rounded-full" />
        <span class="ml-3 mr-3 block truncate">
          {{ value.title }} 
        </span>
        <span class="rounded-full border border-gray-300 px-3 py-0.5" v-if="value.bookmarked">
          gemerkt
        </span>
        <span v-if="value.score">
          <Similarity :value="value.score" />
        </span>
      </span>
      <span v-else="value" class="flex items-center text-gray-500">
          Bitte Methode auswählen...
      </span>
      <span class="pointer-events-none absolute inset-y-0 right-0 ml-3 flex items-center pr-2">
        <ChevronUpDownIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
      </span>
    </ListboxButton>
      <transition leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100" leave-to-class="opacity-0">
        <ListboxOptions class="listbox-options">
          <ListboxOption as="template" v-for="practice in sortedResults" v-slot="{ active, selected }" :key="practice.id" :value="practice">
            <li :class="[active ? 'text-white bg-teal-600' : 'text-gray-900', 'relative cursor-default select-none py-2 pl-3 pr-9']">
              <div class="flex items-center">
                <Twemoji :code="practice.emoji" class="h-6 w-6 flex-shrink-0 rounded-full" />
                <span :class="[selected ? 'font-semibold' : 'font-normal', 'ml-3 mr-3 block truncate']">
                  {{ practice.title }}
                </span>
                <span class="rounded-full border border-gray-300 px-3 py-0.5" v-if="practice.bookmarked">
                  gemerkt
                </span>
                <span v-if="practice.score">
                  <Similarity :value="practice.score" />
                </span>
              </div>
              <span v-if="selected" :class="[active ? 'text-white' : 'text-teal-600', 'absolute inset-y-0 right-0 flex items-center pr-4']">
                <CheckIcon class="h-5 w-5" aria-hidden="true" />
              </span>
            </li>
          </ListboxOption>
        </ListboxOptions>
    </transition>
  </Listbox>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useFetchResources } from '@/client/actions';
import client from '@/client';

const props = defineProps({'modelValue': String});
const emits = defineEmits(['update:modelValue']);

const source = computed(() => props.modelValue);
const value = ref(null);

const store = useStore();
const parent = computed(() => store.getters['context/context'].id);
const target = computed(() => '/api/podcasts/' + parent.value + '/practices');

const action = useFetchResources(target, {records: 1000});

const { results, fetch, fetching } = action;
const sortedResults = computed(()=>results.value.sort((a, b) => {
    return (b.bookmarked === a.bookmarked) ? 0 : b.bookmarked ? 1 : -1;
}));

const mounted = onMounted(() => fetch());
const emitter = (change) => emits('update:modelValue', change?.id);
const changed = watch(value, emitter);

const lookup = (id) => results.value.find(r => r.id == id);
const reset = (id) => value.value = lookup(id);
const loaded = watch(results, (a)  => reset(source.value));
</script>

<style scoped>
.listbox-button
{
  @apply relative w-full py-2 pl-3 pr-10;
  @apply border border-gray-300;
  @apply bg-white shadow-sm rounded-md;
  @apply text-left sm:text-sm;
  @apply focus:border-teal-500 focus:outline-none;
  @apply focus:ring-1 focus:ring-teal-500;
  @apply cursor-default;
};

.listbox-options
{
  @apply absolute py-1 z-10 mt-1 max-h-60 w-auto;
  @apply rounded-md bg-white shadow-lg;
  @apply overflow-auto;
  @apply text-base;
  @apply ring-1 ring-black ring-opacity-5;
  @apply focus:outline-none sm:text-sm;
};
</style>
